import type { FC } from "react";
import { useEffect, useState } from "react";
import { Button, Modal, Text } from "@uhg-harmony/react";
import type { ModalProps } from "@uhg-harmony/react";
import { createLogger } from "../../features/notifications/utils/logger";
import { useAppSelector, useAppDispatch } from "../../redux/hooks";
import { templatesActions } from "../../redux/templates/templatesSlice";
import { selectTemplatesLoading } from "../../redux/templates/templatesTreeSelectors";
import { useAuth } from "react-oidc-context";
import { sendTemplateNotification } from "../../features/notifications/utils/sendTemplateNotification";
import type { ActivateTemplateVersionResponse } from "../../redux/templates/templatesTypes";
import { ErrorContainer } from "./shared";
import styles from "./ActivateVersion.module.css";
import { selectFolderName, selectCategory, selectAccountId } from "../../redux/metadata/metadataSlice";

const logger = createLogger("ActivateVersion");

type ActivateVersionModalProps = ModalProps & {
  open: boolean;
  onClose: () => void;
  onConfirm?: (result: ActivateTemplateVersionResponse) => void;
  templateName?: string;
  versionLabel?: string; // e.g. "V2"
  action?: "activate" | "deactivate";
  activateInfo?: {
    templateRowId: string;        // templates.row_id
    templateVersionRowId: string; // template_versions.template_version_id
  } | null;
};

export const ActivateVersionModal: FC<ActivateVersionModalProps> = ({
  title,
  open,
  onClose,
  onConfirm,
  templateName,
  versionLabel,
  action = "activate",
  activateInfo,
}) => {
  const dispatch = useAppDispatch();
  const auth = useAuth();

  const [isActivating, setIsActivating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const isLoading = useAppSelector(selectTemplatesLoading);
  const templatesState = useAppSelector((state) => state.templates);
  const lastOperation = useAppSelector((state) => state.templates.lastOperation);
  const folderName = useAppSelector(selectFolderName);
  const category = useAppSelector(selectCategory);
  const accountId = useAppSelector(selectAccountId);

  // Clear error & activation flag when modal opens
  useEffect(() => {
    if (open) {
      setErrorMessage("");
      setIsActivating(false);
    }
  }, [open]);

  // Monitor activate operation response
  useEffect(() => {
    if (
      !isActivating ||
      !lastOperation ||
      lastOperation.name !== "activateTemplateVersion"
    ) {
      return;
    }

    // Success
    if (!isLoading && lastOperation.result && !templatesState.error) {
      sendTemplateNotification(
        auth.user?.access_token,

        action === "deactivate"
          ? "Template Version Deactivated Successfully"
          : "Template Version Activated Successfully",
        action === "deactivate"
          ? `${versionLabel ?? "Version"} of template "${templateName?.trim() || "Unnamed"}" has been deactivated. Folder: ${folderName}`
          : `${versionLabel ?? "Version"} of template "${templateName?.trim() || "Unnamed"}" is now active. Folder: ${folderName}`,
        category,

        (error) => logger.error("Failed to send activation notification:", error),
      );

      setIsActivating(false);
      onConfirm?.(lastOperation.result as ActivateTemplateVersionResponse);
      onClose();
      return;
    }

    // Failure
    if (!isLoading && templatesState.error) {
      setErrorMessage(`Failed to ${action === "deactivate" ? "deactivate" : "activate"} version: ${templatesState.error}`);
      setIsActivating(false);
    }
  }, [
    isActivating,
    isLoading,
    lastOperation,
    templatesState.error,
    templateName,
    versionLabel,
    folderName,
    category,
    accountId,
    action,
    auth.user?.access_token,
    auth.user?.profile?.sub,
    onConfirm,
    onClose,
  ]);

  const handleConfirm = () => {
    if (!activateInfo?.templateRowId || !activateInfo?.templateVersionRowId) {
      setErrorMessage("Missing template version information");
      return;
    }
    if (!auth.user?.access_token) {
      setErrorMessage("Authentication required");
      return;
    }

    setIsActivating(true);
    setErrorMessage("");

    dispatch(
      templatesActions.activateTemplateVersionRequested({
        token: auth.user.access_token,
        templateRowId: activateInfo.templateRowId,
        templateVersionRowId: activateInfo.templateVersionRowId,
        action,
      }),
    );
  };

  return (
    <>
      {open && (
        <Modal open={open} title={title} aria-label={typeof title === 'string' ? title : 'Activate Version'} onClose={onClose}>
          <Modal.Body>
            {errorMessage && (
              <ErrorContainer>
                <strong>Error:</strong> {errorMessage}
              </ErrorContainer>
            )}
            <Text
              size="xlarge"
              className={styles.confirmationText}
            >
              Are you sure you want to {action === "deactivate" ? "deactivate" : "activate"}{" "}
              <strong>{versionLabel ?? "this version"}</strong> for template{" "}
              <strong>{templateName?.trim() || "this template"}</strong>?
            </Text>
            <Text size="large" className={styles.descriptionText}>
              {action === "deactivate"
                ? "The selected version will be marked as inactive."
                : "The selected version will be marked as Active and all other published versions of this template will be deactivated."}
            </Text>
            {action === "deactivate" && (
              <Text
                size="large"
                className={styles.deactivateNote}
              >
                Note: Deactivating this version will leave the template with no
                active version. You can reactivate a version at any time.
              </Text>
            )}
          </Modal.Body>

          <Modal.Actions>
            <Button
              variant="secondary"
              onPress={onClose}
              isDisabled={isActivating}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={handleConfirm}
              isDisabled={isActivating}
            >
              {isActivating
                ? (action === "deactivate" ? "Deactivating..." : "Activating...")
                : (action === "deactivate" ? "Deactivate" : "Activate")}
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </>
  );
};